import { useEffect, useState } from "react";
import { Volume2, RefreshCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { IDS } from "@/lib/testIds";
import { GAME_ITEMS, LETTERS, shuffle, randomFrom } from "@/lib/gameData";
import { GAME_FEEDBACK_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

// Build a round: choose a word, take its first letter as the "sound", show 4 letter options
function newRound() {
  const item = randomFrom(GAME_ITEMS);
  const correct = item.word[0];
  const distractors = shuffle(LETTERS.filter((l) => l !== correct)).slice(0, 3);
  const options = shuffle([correct, ...distractors]);
  return { item, correct, options };
}

export default function LetterSoundMatch() {
  const [round, setRound] = useState(newRound);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState(null); // { correct: bool, letter: string }
  const [locked, setLocked] = useState(false);
  const { award } = useAuth();

  useEffect(() => {
    // Auto-play the sound on new round. Speak the example word twice — its
    // initial phoneme IS the sound the learner should identify. Speaking the
    // raw phonetic ("k", "kw") makes TTS read letter names, which is wrong.
    const t = setTimeout(() => {
      const example = phonic(round.correct);
      speak(`${example}. ${example}.`, { rate: 0.85 });
    }, 200);
    return () => clearTimeout(t);
  }, [round]);

  const handlePick = (letter) => {
    if (locked) return;
    setLocked(true);
    const correct = letter === round.correct;
    setAttempts((a) => a + 1);
    if (correct) {
      setScore((s) => s + 1);
      award(10, "match");
      setFeedback({ correct: true, letter });
      toast.success(`Yes! "${letter}" is right.`);
      setTimeout(() => {
        setFeedback(null);
        setLocked(false);
        setRound(newRound());
      }, GAME_FEEDBACK_MS);
    } else {
      setFeedback({ correct: false, letter });
      toast.error(`Try again — that was ${round.correct}.`);
      setTimeout(() => {
        setFeedback(null);
        setLocked(false);
      }, GAME_FEEDBACK_MS);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div
          data-testid={IDS.games.match.score}
          className="inline-flex items-center gap-2 bg-sky-50 text-sky-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid={IDS.games.match.next}
          onClick={() => setRound(newRound())}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New round
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
          Which letter makes this sound?
        </p>
        <button
          data-testid={IDS.games.match.playSound}
          onClick={() => {
            const example = phonic(round.correct);
            speak(`${example}. ${example}.`, { rate: 0.85 });
          }}
          className="mt-2 inline-flex items-center gap-2 rounded-full px-6 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold shadow-md hover:shadow-lg transition-all"
        >
          <Volume2 className="w-5 h-5" /> Play sound
        </button>
        <p className="mt-3 text-xs text-slate-400">
          Hint: think of a word like &ldquo;{round.item.word}&rdquo; {round.item.emoji}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {round.options.map((letter, i) => {
          const isWrong = feedback && !feedback.correct && feedback.letter === letter;
          const isRight = feedback && feedback.correct && feedback.letter === letter;
          return (
            <button
              key={letter}
              data-testid={IDS.games.match.option(i)}
              onClick={() => handlePick(letter)}
              className={`aspect-square rounded-3xl border-2 font-display text-6xl font-semibold transition-all
                ${isRight ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop" : ""}
                ${isWrong ? "bg-rose-100 border-rose-300 text-rose-600 phonx-shake" : ""}
                ${!feedback ? "bg-white border-slate-200 hover:border-sky-400 hover:text-sky-500 hover:-translate-y-1 shadow-sm hover:shadow-lg" : ""}
              `}
            >
              {letter}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Example word for each starting letter. Passed to TTS so the learner hears
// the sound naturally inside a real word (browser TTS can't reliably speak
// isolated phonic strings like "k" or "kw").
function phonic(letter) {
  const map = {
    A: "apple",
    B: "ball",
    C: "cat",
    D: "dog",
    E: "egg",
    F: "fish",
    G: "goat",
    H: "hat",
    I: "igloo",
    J: "juice",
    K: "kite",
    L: "lion",
    M: "moon",
    N: "nest",
    O: "orange",
    P: "pig",
    Q: "queen",
    R: "rain",
    S: "sun",
    T: "tree",
    U: "umbrella",
    V: "van",
    W: "web",
    X: "box",
    Y: "yarn",
    Z: "zebra",
  };
  return map[letter] || letter;
}
